<template>
  <div
    class="gauge"
    role="img"
    :aria-label="$t('complete_listing_gauge_aria', { percent: displayNumber })"
  >
    <svg viewBox="0 0 100 100" class="gauge-svg">
      <circle class="gauge-track" cx="50" cy="50" r="42" fill="transparent" />
      <circle
        v-if="displayNumber > 0"
        class="gauge-fill"
        cx="50"
        cy="50"
        r="42"
        fill="transparent"
        :stroke-dasharray="`${progressLength} ${circumference}`"
        transform="rotate(-90 50 50)"
      />
    </svg>
    <span class="gauge-value">{{ displayNumber }}%</span>
  </div>
</template>

<script>
const ANIMATION_START_DELAY_MS = 400;
const ANIMATION_TICK_MS = 40;
const ANIMATION_STEPS = 10;

export default {
  name: 'AdRankingGauge',

  props: {
    /**
     * Target percentage to display (0–100). Comes from the API
     * (`proposition.current` in /api/my-proposition) — the gauge no
     * longer derives its own number from proposition state.
     */
    percent: {
      type: Number,
      default: 0
    },
    animate: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      hasLoaded: !this.animate,
      displayNumber: this.animate ? 0 : this.percent,
      tickInterval: null,
      startAnimationTimeout: null,
      // Matches viewBox "0 0 100 100" + r="42" in the template
      circumference: 2 * Math.PI * 42
    };
  },

  computed: {
    targetPercent() {
      if (!this.hasLoaded) return 0;
      return Math.max(0, Math.min(100, this.percent || 0));
    },

    progressLength() {
      return (this.targetPercent / 100) * this.circumference;
    }
  },

  watch: {
    targetPercent() {
      this.runDisplayNumberAnimation();
    }
  },

  mounted() {
    if (this.animate) {
      this.startAnimationTimeout = setTimeout(() => {
        this.hasLoaded = true;
      }, ANIMATION_START_DELAY_MS);
    } else {
      this.displayNumber = this.targetPercent;
    }
  },

  beforeDestroy() {
    clearTimeout(this.startAnimationTimeout);
    clearInterval(this.tickInterval);
  },

  methods: {
    runDisplayNumberAnimation() {
      clearInterval(this.tickInterval);
      if (this.targetPercent === this.displayNumber) return;

      this.tickInterval = window.setInterval(() => {
        if (this.displayNumber === this.targetPercent) {
          clearInterval(this.tickInterval);
          return;
        }
        const change = Math.max(
          1,
          Math.ceil(
            Math.abs(this.targetPercent - this.displayNumber) / ANIMATION_STEPS
          )
        );
        this.displayNumber +=
          this.targetPercent > this.displayNumber ? change : -change;
      }, ANIMATION_TICK_MS);
    }
  }
};
</script>

<style lang="scss" scoped>
.gauge {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gauge-svg {
  display: block;
  width: 100%;
  height: 100%;
}

.gauge-track {
  stroke: #e5e7eb;
  stroke-width: 8;
}

.gauge-fill {
  stroke: var(--Primary-Color);
  stroke-width: 8;
  stroke-linecap: round;
  transition: stroke-dasharray 0.6s ease;
}

.gauge-value {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
</style>
